import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import axios from "axios";
import {
  BookOpen,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  Hash,
  AlertCircle,
  CheckCircle2,
  Bookmark,
  Star,
  TrendingUp,
  Image,
  Video,
  FileText,
  Eye,
  Tag,
} from "lucide-react";
import { toast } from "react-toastify";
import { Editor } from "@tinymce/tinymce-react";
import { server, tinyMceKey } from "../server";

const AddNotes = () => {
  const [topics, setTopics] = useState([""]);
  const [topicsList, setTopicsList] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(false);
  const [isLoadingChapters, setIsLoadingChapters] = useState(false);
  const [isLoadingTopics, setIsLoadingTopics] = useState(false);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      title: "",
      isPublished: false,
      isFeatured: false,
      subjectId: "",
      chapterId: "",
      topicId: "",
    },
  });

  // Fetch subjects on component mount
  const fetchSubjects = async () => {
    try {
      setIsLoadingSubjects(true);
      const { data } = await axios.get(`${server}/subject/all`, {
        params: {
          pageNo: 1,
          pageSize: 100,
        },
      });
      setSubjects(data?.data || []);
    } catch (error) {
      toast.error("Failed to load subjects");
      console.error("Error fetching subjects:", error);
    } finally {
      setIsLoadingSubjects(false);
    }
  };

  // Fetch chapters when subject is selected
  const fetchChapters = async (subjectId) => {
    try {
      setIsLoadingChapters(true);
      const { data } = await axios.get(`${server}/chapter/all`, {
        params: {
          subjectId,
        },
      });
      setChapters(data?.data || []);
    } catch (error) {
      toast.error("Failed to load chapters");
      console.error("Error fetching chapters:", error);
    } finally {
      setIsLoadingChapters(false);
    }
  };

  // Fetch topics when chapter is selected
  const fetchTopics = async (subjectId, chapterId) => {
    try {
      setIsLoadingTopics(true);
      const { data } = await axios.get(`${server}/topics/all`, {
        params: {
          pageNo: 1,
          pageSize: 100,
          subjectId,
          chapterId,
        },
      });
      setTopicsList(data?.data || []);
    } catch (error) {
      toast.error("Failed to load topics");
      console.error("Error fetching topics:", error);
    } finally {
      setIsLoadingTopics(false);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (selectedSubject) {
      fetchChapters(selectedSubject);
      setSelectedChapter("");
      setSelectedTopic("");
      setTopicsList([]);
    } else {
      setChapters([]);
      setSelectedChapter("");
      setSelectedTopic("");
      setTopicsList([]);
    }
  }, [selectedSubject]);

  useEffect(() => {
    if (selectedSubject && selectedChapter) {
      fetchTopics(selectedSubject, selectedChapter);
      setSelectedTopic("");
    } else {
      setTopicsList([]);
      setSelectedTopic("");
    }
  }, [selectedChapter]);

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);

      // Filter out empty topics
      const validTopics = topics.filter((topic) => topic.trim() !== "");

      if (validTopics.length === 0 && !selectedTopic) {
        toast.error("Please add at least one topic or select from dropdown");
        return;
      }

      if (!selectedSubject) {
        toast.error("Please select a subject");
        return;
      }

      if (!selectedChapter) {
        toast.error("Please select a chapter");
        return;
      }

      const content = editorRef.current ? editorRef.current.getContent() : "";

      if (!content.trim()) {
        toast.error("Please add blog content");
        return;
      }

      const formData = new FormData();
      formData.append("title", data.title);
      formData.append("content", content);
      formData.append("subjectId", selectedSubject);
      formData.append("chapterId", selectedChapter);
      formData.append("topicId", selectedTopic);

      formData.append("isPublished", data.isPublished);
      formData.append("isFeatured", data.isFeatured);
      formData.append("topics", JSON.stringify(validTopics));

      // Append media files
      mediaFiles.forEach((file) => {
        formData.append("media", file.file);
      });

      const { data: resData } = await axios.post(
        `${server}/notes/new`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success(resData?.message || "Saved!");
      resetForm();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    reset();
    setTopics([""]);
    setSelectedSubject("");
    setSelectedChapter("");
    setSelectedTopic("");
    setMediaFiles([]);
    if (editorRef.current) {
      editorRef.current.setContent("");
    }
  };

  // Handle topic input
  const handleTopicChange = (index, value) => {
    const newTopics = [...topics];
    newTopics[index] = value;
    setTopics(newTopics);
  };

  const addTopic = () => {
    setTopics([...topics, ""]);
  };

  const removeTopic = (index) => {
    if (topics.length > 1) {
      const newTopics = topics.filter((_, i) => i !== index);
      setTopics(newTopics);
    }
  };

  // Handle subject selection
  const handleSubjectChange = (subjectId) => {
    setSelectedSubject(subjectId);
    setValue("subjectId", subjectId);
  };

  // Handle chapter selection
  const handleChapterChange = (chapterId) => {
    setSelectedChapter(chapterId);
    setValue("chapterId", chapterId);
  };

  // Handle topic selection from dropdown
  const handleTopicSelect = (topicId) => {
    setSelectedTopic(topicId);
    setValue("topicId", topicId);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const validFiles = files.filter(
      (file) =>
        file.type.startsWith("image/") ||
        file.type.startsWith("video/") ||
        file.type === "application/pdf"
    );

    const newFiles = validFiles.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      type: file.type.startsWith("image/")
        ? "image"
        : file.type.startsWith("video/")
        ? "video"
        : "document",
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
    }));

    setMediaFiles((prev) => [...prev, ...newFiles]);
  };

  const removeMediaFile = (id) => {
    setMediaFiles((prev) =>
      prev.filter((file) => {
        if (file.id === id) {
          URL.revokeObjectURL(file.url);
        }
        return file.id !== id;
      })
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const buttonVariants = {
    initial: { scale: 1 },
    tap: { scale: 0.95 },
    hover: { scale: 1.05 },
  };

  const getSelectedSubjectName = () => {
    const subject = subjects.find(
      (sub) => sub.id === parseInt(selectedSubject)
    );
    return subject ? subject.subject : "";
  };

  const getSelectedChapterName = () => {
    const chapter = chapters.find(
      (chap) => chap.id === parseInt(selectedChapter)
    );
    return chapter ? chapter.chapter : "";
  };

  const getSelectedTopicName = () => {
    const topic = topicsList.find((top) => top.id === parseInt(selectedTopic));
    return topic ? topic.topic : "";
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <motion.div
        className="max-w-4xl mx-auto"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Header */}
        <motion.div className="text-center mb-8" variants={itemVariants}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-br from-purple-600 to-blue-700 rounded-2xl">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white">Create New Blog</h1>
          </div>
          <p className="text-gray-400 text-lg">
            Write and publish your educational content with rich media
          </p>
        </motion.div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Subject, Chapter & Topic Selection */}
          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-700"
            variants={itemVariants}
          >
            <label className="block text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Bookmark className="w-5 h-5 text-blue-400" />
              Select Subject, Chapter & Topic *
            </label>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Subject Selection */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Subject
                </label>
                {isLoadingSubjects ? (
                  <div className="flex items-center justify-center py-4">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      <RefreshCw className="w-5 h-5 text-gray-400" />
                    </motion.div>
                    <span className="ml-2 text-gray-400">
                      Loading subjects...
                    </span>
                  </div>
                ) : (
                  <select
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 text-white bg-gray-700 ${
                      errors.subjectId
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                        : "border-gray-600 focus:border-blue-500 focus:ring-blue-500/20"
                    }`}
                    value={selectedSubject}
                    onChange={(e) => handleSubjectChange(e.target.value)}
                  >
                    <option value="">Choose a subject</option>
                    {subjects.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.icon} {subject.subject}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Chapter Selection */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Chapter
                </label>
                {isLoadingChapters ? (
                  <div className="flex items-center justify-center py-4">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      <RefreshCw className="w-5 h-5 text-gray-400" />
                    </motion.div>
                    <span className="ml-2 text-gray-400">
                      Loading chapters...
                    </span>
                  </div>
                ) : (
                  <select
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 text-white bg-gray-700 ${
                      errors.chapterId
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                        : "border-gray-600 focus:border-green-500 focus:ring-green-500/20"
                    }`}
                    value={selectedChapter}
                    onChange={(e) => handleChapterChange(e.target.value)}
                    disabled={!selectedSubject}
                  >
                    <option value="">Choose a chapter</option>
                    {chapters.map((chapter) => (
                      <option key={chapter.id} value={chapter.id}>
                        {chapter.chapter}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Topic Selection */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Topic (Optional)
                </label>
                {isLoadingTopics ? (
                  <div className="flex items-center justify-center py-4">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      <RefreshCw className="w-5 h-5 text-gray-400" />
                    </motion.div>
                    <span className="ml-2 text-gray-400">
                      Loading topics...
                    </span>
                  </div>
                ) : (
                  <select
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 text-white bg-gray-700 ${
                      errors.topicId
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                        : "border-gray-600 focus:border-orange-500 focus:ring-orange-500/20"
                    }`}
                    value={selectedTopic}
                    onChange={(e) => handleTopicSelect(e.target.value)}
                    disabled={!selectedChapter}
                  >
                    <option value="">Choose a topic</option>
                    {topicsList.map((topic) => (
                      <option key={topic.id} value={topic.id}>
                        {topic.topic}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {(selectedSubject || selectedChapter || selectedTopic) && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 p-3 bg-blue-500/20 rounded-lg border border-blue-500/30"
              >
                <p className="text-sm text-blue-300">
                  Selected:{" "}
                  <span className="font-semibold">
                    {getSelectedSubjectName()}
                    {selectedChapter && ` → ${getSelectedChapterName()}`}
                    {selectedTopic && ` → ${getSelectedTopicName()}`}
                  </span>
                </p>
              </motion.div>
            )}
          </motion.div>

          {/* Notes Title */}
          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-700"
            variants={itemVariants}
          >
            <label className="block text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Hash className="w-5 h-5 text-green-400" />
              Notes Title *
            </label>
            <input
              type="text"
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 text-white placeholder-gray-500 bg-gray-700 ${
                errors.title
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-600 focus:border-green-500 focus:ring-green-500/20"
              }`}
              placeholder="Enter an engaging notes title..."
              {...register("title", {
                required: "Blog title is required",
                minLength: {
                  value: 5,
                  message: "Title must be at least 5 characters",
                },
              })}
            />
            {errors.title && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-2 text-sm text-red-400 flex items-center gap-1"
              >
                <AlertCircle className="w-4 h-4" />
                {errors.title.message}
              </motion.p>
            )}
          </motion.div>

          {/* Notes Content with TinyMCE */}
          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-700"
            variants={itemVariants}
          >
            <label className="block text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-400" />
              Notes Content *
            </label>

            <div className="border border-gray-600 rounded-lg overflow-hidden">
              <Editor
                apiKey={tinyMceKey}
                onInit={(evt, editor) => (editorRef.current = editor)}
                placeholder="Start writing your amazing nites content here..."
                init={{
                  height: 500,
                  menubar: true,
                  plugins: [
                    "advlist",
                    "autolink",
                    "lists",
                    "link",
                    "image",
                    "charmap",
                    "preview",
                    "anchor",
                    "searchreplace",
                    "visualblocks",
                    "code",
                    "fullscreen",
                    "insertdatetime",
                    "media",
                    "table",
                    "code",
                    "help",
                    "wordcount",
                  ],
                  toolbar:
                    "undo redo | blocks | bold italic underline | " +
                    "alignleft aligncenter alignright alignjustify | " +
                    "bullist numlist outdent indent | link image media | " +
                    "forecolor backcolor | code | fullscreen | preview | help",
                  skin: "oxide-dark",
                  content_css: "dark",
                  content_style: `
                    body { 
                      background: #1f2937; 
                      color: #f9fafb; 
                      font-family: Inter, sans-serif; 
                      font-size: 14px; 
                      line-height: 1.6;
                    }
                    p { margin: 0 0 1em 0; }
                    h1, h2, h3, h4, h5, h6 { color: #f9fafb; }
                  `,
                  images_upload_handler: async (blobInfo) => {
                    return new Promise((resolve) => {
                      const reader = new FileReader();
                      reader.readAsDataURL(blobInfo.blob());
                      reader.onloadend = () => {
                        resolve(reader.result);
                      };
                    });
                  },
                }}
              />
            </div>
          </motion.div>

          {/* Topics Management */}
          {/* <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-700"
            variants={itemVariants}
          >
            <label className="block text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-orange-400" />
              Blog Topics
            </label>
            <p className="text-gray-400 text-sm mb-4">
              Add custom topics or select from dropdown above
            </p>

            <div className="space-y-3">
              {topics.map((topic, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex gap-3"
                >
                  <input
                    type="text"
                    value={topic}
                    onChange={(e) => handleTopicChange(index, e.target.value)}
                    className="flex-1 px-4 py-3 border-2 border-gray-600 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all duration-200 text-white placeholder-gray-500 bg-gray-700"
                    placeholder={`Custom Topic ${index + 1}...`}
                  />
                  {topics.length > 1 && (
                    <motion.button
                      type="button"
                      onClick={() => removeTopic(index)}
                      className="px-4 py-3 bg-red-500/20 border-2 border-red-500/30 text-red-400 rounded-xl hover:bg-red-500/30 transition-all duration-200"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Trash2 className="w-5 h-5" />
                    </motion.button>
                  )}
                </motion.div>
              ))}
            </div>

            <motion.button
              type="button"
              onClick={addTopic}
              className="mt-4 flex items-center gap-2 px-4 py-3 bg-orange-500/20 border-2 border-orange-500/30 text-orange-400 rounded-xl hover:bg-orange-500/30 transition-all duration-200 w-full justify-center"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Plus className="w-5 h-5" />
              Add Another Topic
            </motion.button>
          </motion.div> */}

          {/* Media Upload */}
          {/* <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-700"
            variants={itemVariants}
          >
            <label className="block text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Image className="w-5 h-5 text-pink-400" />
              Media Files
            </label>
            <p className="text-gray-400 text-sm mb-4">
              Upload images, videos, or documents to enhance your blog
            </p>

            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                isDragging
                  ? "border-pink-500 bg-pink-900 bg-opacity-20 scale-105"
                  : "border-gray-600 bg-gray-700 hover:border-pink-400 hover:bg-gray-600"
              }`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="space-y-3">
                <div className="text-4xl">📁</div>
                <div>
                  <p className="text-lg font-medium text-gray-200">
                    {isDragging
                      ? "Drop your files here!"
                      : "Drag and drop files here"}
                  </p>
                  <p className="text-gray-400 mt-1">or click to select files</p>
                </div>
                <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Image className="w-4 h-4" /> Images
                  </span>
                  <span className="flex items-center gap-1">
                    <Video className="w-4 h-4" /> Videos
                  </span>
                  <span className="flex items-center gap-1">
                    <FileText className="w-4 h-4" /> Documents
                  </span>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,.pdf,.doc,.docx"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {mediaFiles.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-white mb-3">
                  Uploaded Files ({mediaFiles.length})
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {mediaFiles.map((media) => (
                    <motion.div
                      key={media.id}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="relative group bg-gray-700 rounded-lg overflow-hidden border border-gray-600 hover:shadow-lg transition-shadow duration-200"
                    >
                      {media.type === "image" && (
                        <img
                          src={media.url}
                          alt="Preview"
                          className="w-full h-24 object-cover"
                        />
                      )}
                      {media.type === "video" && (
                        <div className="relative">
                          <video className="w-full h-24 object-cover">
                            <source src={media.url} type={media.file.type} />
                          </video>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-black bg-opacity-50 rounded-full p-2">
                              <Video className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        </div>
                      )}
                      {media.type === "document" && (
                        <div className="w-full h-24 bg-gray-600 flex items-center justify-center">
                          <FileText className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      <div className="p-2">
                        <p
                          className="text-xs font-medium text-gray-200 truncate"
                          title={media.name}
                        >
                          {media.name}
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatFileSize(media.size)}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 focus:opacity-100 focus:outline-none hover:bg-red-700 text-sm"
                        onClick={() => removeMediaFile(media.id)}
                        title="Remove file"
                      >
                        ×
                      </button>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </motion.div> */}

          {/* Publishing Options */}
          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-700"
            variants={itemVariants}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Publish Toggle */}
              <label className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/20 rounded-lg border border-green-500/30">
                    <Eye className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <span className="block text-sm font-semibold text-white">
                      Publish Immediately
                    </span>
                    <span className="block text-sm text-gray-400 mt-1">
                      Make blog visible to readers
                    </span>
                  </div>
                </div>
                <input
                  type="checkbox"
                  className="hidden"
                  {...register("isPublished")}
                />
                <div
                  className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                    watch("isPublished") ? "bg-green-500" : "bg-gray-600"
                  }`}
                >
                  <motion.div
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-lg ${
                      watch("isPublished") ? "left-7" : "left-1"
                    }`}
                    layout
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                </div>
              </label>

              {/* Featured Toggle */}
              <label className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
                    <Star className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <span className="block text-sm font-semibold text-white">
                      Mark as Featured
                    </span>
                    <span className="block text-sm text-gray-400 mt-1">
                      Highlight this blog
                    </span>
                  </div>
                </div>
                <input
                  type="checkbox"
                  className="hidden"
                  {...register("isFeatured")}
                />
                <div
                  className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                    watch("isFeatured") ? "bg-yellow-500" : "bg-gray-600"
                  }`}
                >
                  <motion.div
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-lg ${
                      watch("isFeatured") ? "left-7" : "left-1"
                    }`}
                    layout
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                </div>
              </label>
            </div>

            {(watch("isPublished") || watch("isFeatured")) && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-4 p-3 bg-blue-500/20 rounded-lg border border-blue-500/30"
              >
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-blue-400" />
                  <span className="text-blue-300">
                    {watch("isPublished") &&
                      "Blog will be published immediately"}
                    {watch("isPublished") && watch("isFeatured") && " and "}
                    {watch("isFeatured") && "marked as featured"}
                  </span>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Form Actions */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 pt-6"
            variants={itemVariants}
          >
            <motion.button
              type="submit"
              disabled={
                isSubmitting ||
                !selectedSubject ||
                !selectedChapter ||
                !watch("title")
              }
              className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-br from-purple-600 to-blue-700 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              variants={buttonVariants}
              whileHover={
                !isSubmitting &&
                selectedSubject &&
                selectedChapter &&
                watch("title")
                  ? { scale: 1.02 }
                  : {}
              }
              whileTap={
                !isSubmitting &&
                selectedSubject &&
                selectedChapter &&
                watch("title")
                  ? { scale: 0.98 }
                  : {}
              }
            >
              {isSubmitting ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <RefreshCw className="w-5 h-5" />
                  </motion.div>
                  Submitting Notes...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Submit Notes
                </>
              )}
            </motion.button>

            <motion.button
              type="button"
              onClick={resetForm}
              className="flex items-center justify-center gap-2 px-6 py-4 border-2 border-gray-600 text-gray-300 font-semibold rounded-xl hover:border-gray-500 hover:bg-gray-700 transition-all duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <RefreshCw className="w-5 h-5" />
              Reset Form
            </motion.button>
          </motion.div>
        </form>
      </motion.div>
    </div>
  );
};

export default AddNotes;
